import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, forwardRef } from '@angular/core';
import { RequestService } from '@df/api/request/request.service';
import { App } from '@df/core/app';
import { DfDomModule } from '@df/dom/df-dom.module';
import { Ga4TrackClickDirective } from '@df/module-gtm/ga4-track-click.directive';
import { ImageModule } from '@df/module-image/image.module';
import { TimeoutService } from '@df/ng/timeout.service';
import { BasketService } from '@df/session/api/basket/basket.service';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { DfUiModule } from '@df/ui/df-ui.module';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { ICmsModalService } from '@icms/client/assets/modal/icms-modal.service';
import { GtmImpressionTrackDirective } from 'projects/app-default/src/app/module/gtm/gtm-impression-track.directive';
import { GtmTrackProductClickDirective } from 'projects/app-default/src/app/module/gtm/gtm-track-product-click.directive';
import { TooltipComponent } from 'projects/app-default/src/app/ui/common/tooltip/tooltip.component';
import { ScrollableComponent } from 'projects/app-default/src/app/ui/scrollable/scrollable.component';
import { NBuilderConfig } from '../../common/builder.config';
import { BuilderAddToBasketComponent } from '../../common/products-setup/add-to-basket/builder-add-to-basket.component';
import { BuilderProductsSetupComponentDef } from '../../common/products-setup/builder-products-setup.component.def';
import { NBuilderProductsSetup } from '../../common/products-setup/builder-products-setup.config';
import { BuilderGalleryComponent, IFocusData, IOptionImageData } from '../../common/products-setup/gallery/builder-gallery.component';
import { BuilderListingToolsComponent } from '../../common/products-setup/listing-tools/builder-listing-tools.component';
import { BuilderProductComponent } from '../../common/products-setup/product/builder-product.component';
import { NOutfitGeneralSetup } from '../../outfit-builder/general-setup/outfit-general-setup.config';
import { NOutfitBuilder } from '../../outfit-builder/outfit-builder.namespace';
import { OutfitBuilderService } from '../outfit-builder.service';

@Component({
    selector: 'outfit-products-setup',
    templateUrl: '../../common/products-setup/builder-products-setup.html',
    styleUrls: ['../../common/products-setup/builder-products-setup.scss'],
    standalone: true,
    imports: [
        forwardRef(() => BuilderAddToBasketComponent),
        BuilderGalleryComponent,
        BuilderListingToolsComponent,
        BuilderProductComponent,
        DfUiModule,
        DfDomModule,
        Ga4TrackClickDirective,
        GridModule,
        GtmImpressionTrackDirective,
        GtmTrackProductClickDirective,
        ImageModule,
        NgClass,
        NgStyle,
        NgTemplateOutlet,
        PriceComponent,
        ScrollableComponent,
        SizeClassDirective,
        TooltipComponent,
        TranslationModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OutfitProductsSetupComponent extends BuilderProductsSetupComponentDef implements OnInit, OnDestroy {
    @Input({ required: true })
    content!: NOutfitBuilder.IPageData;
    @Input()
    storageData!: NOutfitBuilder.IStorageData;
    protected riderOptionElements: NBuilderProductsSetup.IOptionElementData[] = [];
    protected horseOptionElements: NBuilderProductsSetup.IOptionElementData[] = [];
    protected useGA4 = true;
    constructor(
        app: App,
        changeDetectorRef: ChangeDetectorRef,
        cmsModalService: ICmsModalService,
        basketService: BasketService,
        timeoutService: TimeoutService,
        requestService: RequestService,
        private outfitBuilderService: OutfitBuilderService
    ) {
        super(app, changeDetectorRef, cmsModalService, basketService, timeoutService, requestService);
    }
    protected changeToggleState(state: NBuilderProductsSetup.EToggleOptions | undefined = this.getDefaultToggleState()): void {
        if (this.storageData.optionToggleState !== state) {
            this.storageData.optionToggleState = state;
            const currentOption =
                state === NBuilderProductsSetup.EToggleOptions.horse
                    ? this.horseOptionElements?.[0]?.sectionOption
                    : this.riderOptionElements?.[0]?.sectionOption;
            this.changeCurrentOption(currentOption);
        }
    }
    protected reset(): void {
        this.outfitBuilderService.goToStep(this.storageData, NOutfitBuilder.ESteps.configure);
    }
    protected undo(): void {
        if (this.storageData.selection?.[this.storageData.currentSectionOptionId || 0]) {
            this.unselectCurrentOption();
        } else if (!Object.values(this.storageData.selection!).find(v => v)) {
            this.outfitBuilderService.goToStep(this.storageData, NOutfitBuilder.ESteps.configure);
        } else {
            const prevSection =
                this.storageData.history?.prevSelectedSection ||
                Object.keys(this.storageData.selection!)
                    ?.map(Number)
                    ?.find(key => this.storageData.selection![key]);
            if (!this.leftSectionOption.find(option => option.sectionOption === prevSection)) {
                this.changeToggleState(
                    this.toggleState && this.toggleState === NBuilderProductsSetup.EToggleOptions.horse
                        ? NBuilderProductsSetup.EToggleOptions.rider
                        : NBuilderProductsSetup.EToggleOptions.horse
                );
            }
            if (prevSection) {
                this.changeCurrentOption(prevSection);
            }

            this.storageData.history!.prevSelectedSection = undefined;
        }
        this.changeDetectorRef.detectChanges();
    }

    private getDefaultToggleState(): NBuilderProductsSetup.EToggleOptions | undefined {
        return this.storageData.configOption === NOutfitBuilder.EConfigureOptions.riderAndHorse
            ? NBuilderProductsSetup.EToggleOptions.rider
            : this.storageData.configOption === NOutfitBuilder.EConfigureOptions.rider
              ? NBuilderProductsSetup.EToggleOptions.rider
              : NBuilderProductsSetup.EToggleOptions.horse;
    }
    // filter products by section option and remove options without products
    protected initOptionElements(): void {
        // rider
        if (this.storageData.configOption !== NOutfitBuilder.EConfigureOptions.horse) {
            this.riderOptionElements = this.prepareListings(
                this.content.leftSectionRider,
                (data: NBuilderConfig.IStorageData, sectionOptionId) =>
                    !!this.category
                        ? this.outfitBuilderService
                              .getRiderProducts(data as NOutfitBuilder.IStorageData, sectionOptionId)
                              ?.sort((a, b) => this.sortBycategory(a, b))
                        : this.outfitBuilderService.getRiderProducts(data as NOutfitBuilder.IStorageData, sectionOptionId)
            );
        }
        // horse
        if (this.storageData.configOption !== NOutfitBuilder.EConfigureOptions.rider) {
            this.horseOptionElements = this.prepareListings(
                this.content.leftSectionHorse,
                (data: NBuilderConfig.IStorageData, sectionOptionId) =>
                    !!this.category
                        ? this.outfitBuilderService
                              .getHorseProducts(data as NOutfitBuilder.IStorageData, sectionOptionId)
                              .sort((a, b) => this.sortBycategory(a, b))
                        : this.outfitBuilderService.getHorseProducts(data as NOutfitBuilder.IStorageData, sectionOptionId)
            );
        }
        if (!this.storageData.optionToggleState) {
            this.changeToggleState();
        }
    }
    protected selectAdditionalOption(name: string) {
        if (this.storageData.horse && this.storageData.horse.optionName !== name) {
            this.storageData.horse.optionName = name;
            this.changeDetectorRef.detectChanges();
            this.updateStorage();
        }
    }
    protected override isSelectedAdditionalOption(name: string) {
        return this.storageData.horse?.optionName === name;
    }
    get canShowToogle(): boolean {
        return this.storageData.configOption === NOutfitBuilder.EConfigureOptions.riderAndHorse;
    }
    get horseImage(): { image: string; altImage?: string } {
        if (!this.storageData.horse) {
            return { image: '' };
        }
        switch (this.storageData.horse.tabId) {
            case NOutfitGeneralSetup.EHorseType.horse:
                return this.findImage(this.content.horseStepHorseOptions, this.storageData.horse.optionName);
            case NOutfitGeneralSetup.EHorseType.pony:
                return this.findImage(this.content.horseStepPonyOptions, this.storageData.horse.optionName);
        }
        return { image: '' };
    }
    get riderImage(): { image: string; altImage?: string } {
        if (!this.storageData.rider) {
            return { image: '' };
        }
        switch (this.storageData.rider.tabId) {
            case NOutfitGeneralSetup.EGender.women:
                return this.findImage(this.content.riderStepWomenOptions, this.storageData.rider.optionName);
            case NOutfitGeneralSetup.EGender.men:
                return this.findImage(this.content.riderStepMenOptions, this.storageData.rider.optionName);
        }
        return { image: '' };
    }
    get riderImageData(): IOptionImageData {
        return {
            image: this.riderImage.image,
            shift: this.riderShift,
            order: this.riderOptionElements.map(section => ({
                id: section.sectionOption,
                useAdditionalLabel: section.useAdditionalLabel || false,
                hideForAttrOption: section.hideForAttrOption
            })),
            altImage: this.riderImage.altImage
        };
    }
    get horseImageData(): IOptionImageData {
        return {
            image: this.horseImage.image,
            shift: this.horseShift,
            order: this.horseOptionElements.map(section => ({
                id: section.sectionOption,
                useAdditionalLabel: section.useAdditionalLabel || false,
                hideForAttrOption: section.hideForAttrOption
            })),
            altImage: this.horseImage.altImage
        };
    }
    get leftSectionOption(): NBuilderProductsSetup.IOptionElementData[] {
        return this.toggleState === NBuilderProductsSetup.EToggleOptions.horse ? this.horseOptionElements : this.riderOptionElements;
    }
    get currentOption() {
        return this.storageData.optionToggleState === NBuilderProductsSetup.EToggleOptions.horse
            ? this.horseOptionElements.find(option => option.sectionOption === this.storageData.currentSectionOptionId)
            : this.riderOptionElements.find(option => option.sectionOption === this.storageData.currentSectionOptionId);
    }

    get horseShift(): number {
        return this.content.horseShift || 0;
    }

    get riderShift(): number {
        return this.content.riderShift || 0;
    }

    get displayAltImage(): boolean {
        return (
            !!this.riderOptionElements.find(
                option => option.displayAdditionalImage && this.isSectionOptionSelected(option.sectionOption)
            ) ||
            !!this.horseOptionElements.find(option => option.displayAdditionalImage && this.isSectionOptionSelected(option.sectionOption))
        );
    }

    get baseGalleryPosition(): IFocusData {
        return this.storageData.configOption === NOutfitBuilder.EConfigureOptions.riderAndHorse
            ? { posLeft: 0, posTop: 0, scale: this.content.riderAndHorseZoom || 1 }
            : this.storageData.configOption === NOutfitBuilder.EConfigureOptions.rider
              ? { posLeft: this.content.riderLeftShift || 0, posTop: this.content.riderTopShift || 0, scale: this.content.riderZoom || 1 }
              : { posLeft: this.content.horseLeftShift || 0, posTop: this.content.horseTopShift || 0, scale: this.content.horseZoom || 1 };
    }

    get additionalHorseStep(): NBuilderProductsSetup.IAdditionalHorseStep | undefined {
        return {
            id: NBuilderProductsSetup.ADDITIONAL_HORSE_SETP_ID,
            title: 'Horse',
            image: this.content.additionalHorseStepImage || '',
            options: this.content.horseStepHorseOptions
        };
    }

    get isAdditionalStepState(): boolean {
        return this.toggleState === NBuilderProductsSetup.EToggleOptions.horse;
    }
}
